/**
 * AvisoDeFalha — diz que a consulta falhou, em vez de deixar a tela mentindo.
 *
 * O PROBLEMA QUE RESOLVE
 *   Vinte e dois painéis e modais faziam a consulta dentro de um `try/catch`
 *   cujo `catch` só chamava `console.error`. Quando a consulta falhava — rede
 *   caindo, sessão expirada, servidor fora — a tela abria **vazia**. E vazio,
 *   para quem usa, significa "não há nada aqui".
 *
 *   Alguns eram piores: o `AnalyticsPanel` tem `if (loading || !data)` e ficava
 *   em **"Carregando..." para sempre**.
 *
 *   "Não há dado" e "não consegui buscar o dado" são coisas diferentes, e a
 *   interface precisa dizer qual das duas é.
 *
 * POR QUE FLUTUANTE, E NÃO DENTRO DE CADA PAINEL
 *   O certo, no fim, é cada painel mostrar a falha no seu próprio corpo, com o
 *   botão de tentar de novo ali. Mas isso exige mexer na árvore de renderização
 *   de 22 componentes com estruturas diferentes — cirurgia arriscada por um
 *   ganho que este aviso já entrega.
 *
 *   Assim, cada ponto muda **uma linha**, o usuário passa a ser avisado hoje, e
 *   o redesenho substitui isto pelo estado embutido quando chegar em cada
 *   painel — em UM arquivo, não em vinte e dois.
 *
 * Uso:
 *   import { avisarFalha } from '.../shared/AvisoDeFalha';
 *   } catch (e) {
 *     console.error(e);
 *     avisarFalha('Não foi possível carregar as faturas.', carregar);
 *   }
 *
 * E `<AvisosDeFalha />` montado uma vez no shell da aplicação.
 */
import React, { useCallback, useEffect, useState } from 'react';

import { anunciar } from './Anuncio';

const ouvintes = new Set();

/**
 * Mostra um aviso de que a consulta falhou.
 *
 * @param {string} mensagem  O que não foi possível carregar, em português comum.
 * @param {Function} [aoTentarNovamente]  Se passado, aparece o botão "Tentar de novo".
 */
export function avisarFalha(mensagem, aoTentarNovamente = null) {
  const aviso = {
    id: `${Date.now()}-${Math.random()}`,
    mensagem: mensagem || 'Não foi possível carregar estas informações.',
    aoTentarNovamente,
  };
  ouvintes.forEach((o) => o(aviso));
  // Quem usa leitor de tela também precisa saber que a tela não está vazia.
  anunciar(aviso.mensagem, 'assertivo');
}

export default function AvisosDeFalha() {
  const [avisos, setAvisos] = useState([]);

  useEffect(() => {
    const ouvinte = (aviso) => {
      setAvisos((atuais) => {
        // Mesma mensagem repetida (o painel tenta de novo sozinho) não empilha.
        if (atuais.some((a) => a.mensagem === aviso.mensagem)) return atuais;
        return [...atuais, aviso];
      });
    };
    ouvintes.add(ouvinte);
    return () => { ouvintes.delete(ouvinte); };
  }, []);

  const fechar = useCallback((id) => {
    setAvisos((atuais) => atuais.filter((a) => a.id !== id));
  }, []);

  if (avisos.length === 0) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 16,
        right: 16,
        zIndex: 10050,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        maxWidth: 420,
      }}
    >
      {avisos.map((a) => (
        <div
          key={a.id}
          // `role="alert"` faz o leitor de tela anunciar sozinho.
          role="alert"
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12,
            padding: '14px 16px',
            borderRadius: 'var(--radius-md, 8px)',
            border: '1px solid #fecaca',
            backgroundColor: '#fef2f2',
            color: '#991b1b',
            boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)',
            fontSize: 14,
          }}
        >
          <span style={{ flex: 1 }}>
            <strong>{a.mensagem}</strong>
            <span style={{ display: 'block', marginTop: 4, fontSize: 13, color: '#b91c1c' }}>
              A tela pode estar vazia por causa disto — não porque não haja dados.
            </span>
            {a.aoTentarNovamente && (
              <button
                type="button"
                onClick={() => { fechar(a.id); a.aoTentarNovamente(); }}
                style={{
                  marginTop: 10,
                  padding: '6px 14px',
                  border: '1px solid #fca5a5',
                  borderRadius: 'var(--radius-md, 8px)',
                  backgroundColor: '#fff',
                  color: '#991b1b',
                  fontWeight: 'bold',
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                Tentar de novo
              </button>
            )}
          </span>
          <button
            type="button"
            onClick={() => fechar(a.id)}
            aria-label="Fechar aviso"
            style={{
              border: 'none', background: 'none', color: '#991b1b',
              fontSize: 18, lineHeight: 1, cursor: 'pointer', padding: 0,
            }}
          >
            &times;
          </button>
        </div>
      ))}
    </div>
  );
}
